/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import moment from 'moment';
import { Fraction } from '@codegrade/bigfraction';
import { Maybe } from '@codegrade/maybe';
import { AssertionError } from './assert';
import { DateWithoutTime } from './datetime';
import { FractionalIndex } from './fractional_indexes';
// Use strings as otherwise `const a: Order = 2` is allowed.
/** An enum to indicate order between items, LT, EQ, or GT. */
export var Order;
(function (Order) {
    Order["LT"] = "-1";
    Order["EQ"] = "0";
    Order["GT"] = "1";
})(Order || (Order = {}));
/**
 * Translate a number to an Order enum.
 *
 * @param ord The number to translate.
 * @returns The order enum indicating LT, EQ, or GT.
 */
export function numberToOrder(ord) {
    if (ord < 0) {
        return Order.LT;
    }
    else if (ord > 0) {
        return Order.GT;
    }
    else {
        return Order.EQ;
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function orderToNumber(order) {
    switch (order) {
        case Order.GT:
            return 1;
        case Order.LT:
            return -1;
        default:
            return 0;
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function swapOrder(order) {
    switch (order) {
        case Order.GT:
            return Order.LT;
        case Order.LT:
            return Order.GT;
        default:
            return order;
    }
}
const defaultOpts = {
    caseInsensitive: false,
    justCmpNothingIs: Order.GT,
};
/**
 * Compares two sortable values (A, B) and returns how they are ordered A compared to B.
 *
 * @param keyA Value to compare.
 * @param keyB Compared value.
 * @param opts Options for comparison (case sensitivity and how `Nothing` compares to `Just`).
 * @returns The ordering of A compared to B.
 */
function innerCompare(keyA, keyB, opts) {
    if (typeof keyA === 'number') {
        AssertionError.assert(typeof keyB === 'number', () => `${String(keyB)} is not a number`);
        return numberToOrder(keyA - keyB);
    }
    else if (typeof keyA === 'string') {
        AssertionError.assert(typeof keyB === 'string', () => `${String(keyB)} is not a string`);
        return numberToOrder(keyA.localeCompare(keyB, undefined, {
            sensitivity: opts.caseInsensitive ? 'base' : undefined,
        }));
    }
    else if (typeof keyA === 'boolean') {
        AssertionError.assert(typeof keyB === 'boolean', () => `${String(keyB)} is not a boolean`);
        if (keyA === keyB) {
            return Order.EQ;
        }
        else {
            const boolKeyA = keyA;
            return boolKeyA ? Order.GT : Order.LT;
        }
    }
    else if (Maybe.isMaybe(keyA)) {
        AssertionError.assert(Maybe.isMaybe(keyB), () => `${String(keyB)} is not a Maybe.`);
        return keyA.caseOf({
            Just: valueA => keyB.caseOf({
                Just: valueB => innerCompare(valueA, valueB, opts),
                Nothing: () => opts.justCmpNothingIs,
            }),
            Nothing: () => keyB.caseOf({
                Just: () => swapOrder(opts.justCmpNothingIs),
                Nothing: () => Order.EQ,
            }),
        });
    }
    else if (keyA instanceof Date) {
        AssertionError.assert(keyB instanceof Date, () => `${String(keyB)} is not a date`);
        return numberToOrder(keyA.valueOf() - keyB.valueOf());
    }
    else if (moment.isMoment(keyA)) {
        AssertionError.assert(moment.isMoment(keyB), () => `${String(keyB)} is not a moment`);
        // We don't simply use `valueOf()` as we want to be able to sort invalid
        // moments correctly. We follow the example of `Maybe`: invalid moments
        // first. All the `isXXX` methods return `false` if one of the moments
        // is invalid, so that at least makes life a bit easier.
        if (keyA.isBefore(keyB)) {
            return Order.LT;
        }
        else if (keyB.isAfter(keyB)) {
            return Order.GT;
        }
        else if (keyA.isSame(keyB)) {
            return Order.EQ;
        }
        else if (keyA.isValid()) {
            return Order.GT;
        }
        else if (keyB.isValid()) {
            return Order.LT;
        }
        else {
            return Order.EQ;
        }
    }
    else if (moment.isDuration(keyA)) {
        AssertionError.assert(moment.isDuration(keyB), () => `${String(keyB)} is not a duration`);
        return innerCompare(keyA.asMilliseconds(), keyB.asMilliseconds(), opts);
    }
    else if (keyA instanceof DateWithoutTime) {
        AssertionError.assert(keyB instanceof DateWithoutTime, () => `${String(keyB)} is not a DateWithoutTime`);
        return numberToOrder(keyA.compare(keyB));
    }
    else if (FractionalIndex.isFractionalindex(keyA)) {
        AssertionError.assert(FractionalIndex.isFractionalindex(keyB), () => `${String(keyB)} is not a fractional index`);
        if (keyA.isBefore(keyB)) {
            return Order.LT;
        }
        else if (keyA.isEqual(keyB)) {
            return Order.EQ;
        }
        else {
            return Order.GT;
        }
    }
    else if (keyA instanceof Fraction) {
        AssertionError.assert(keyB instanceof Fraction, () => `${String(keyB)} is not a Fraction`);
        return numberToOrder(keyA.compare(keyB));
    }
    else {
        AssertionError.assert(Array.isArray(keyA), () => `${String(keyA)} is not an array`);
        AssertionError.assert(Array.isArray(keyB), () => `${String(keyB)} is not an array`);
        // Pairwise check the given arrays for the ordering.
        const min = Math.min(keyA.length, keyB.length);
        for (let i = 0; i < min; i++) {
            const comparePair = innerCompare(keyA[i], keyB[i], opts);
            if (comparePair !== Order.EQ) {
                return comparePair;
            }
        }
        return innerCompare(keyA.length, keyB.length, opts);
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function compare(keyA, keyB, partialOpts) {
    let opts = defaultOpts;
    if (partialOpts != null) {
        opts = { ...defaultOpts, ...partialOpts };
    }
    return innerCompare(keyA, keyB, opts);
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function compareNullable(keyA, keyB, opts) {
    return compare(Maybe.fromNullable(keyA), Maybe.fromNullable(keyB), opts);
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function isEqual(a, b) {
    return compare(a, b) === Order.EQ;
}
